/**
 * Card — elevated surface against the midnight ground.
 *
 * The default ``glow`` makes the card emit the signature soft gold aura on hover
 * (kept subtle so only one element ever dominates). Use ``interactive`` for tappable
 * product/order cards.
 */
import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "../utils/cn";
import styles from "./Card.module.css";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Emphasis level affecting border/elevation. Defaults to ``"default"``. */
  readonly variant?: "default" | "raised" | "outline";
  /** Emit the gold aura on hover/focus-within. Defaults to ``true``. */
  readonly glow?: boolean;
  /** Add hover lift + pointer cursor for tappable cards. Defaults to ``false``. */
  readonly interactive?: boolean;
  readonly children?: ReactNode;
}

export function Card({
  variant = "default",
  glow = true,
  interactive = false,
  className,
  children,
  ...rest
}: CardProps): JSX.Element {
  return (
    <div
      className={cn(
        styles.card,
        styles[variant],
        glow && "ms-glow-aura",
        interactive && styles.interactive,
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
